import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './App.css';

function App() {
  const [currentType, setCurrentType] = useState('local');
  const [currentSelection, setCurrentSelection] = useState(null);
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [modalPackages, setModalPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPackagesOverlay, setShowPackagesOverlay] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);

  const PROXY_URL = 'https://app.techrisepos.com/api/esim';

  // API request function
  const makeApiRequest = useCallback(async (params, endpoint = 'api/airalo-proxy', retries = 3, retryDelay = 1000) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        console.log(`🔄 API Request to ${endpoint} (Attempt ${attempt}/${retries}):`, params);
        
        const response = await axios.post(`${PROXY_URL}/${endpoint}`, {
          id: "1",
          token: "ZnNoSXgybUpibFhwQW4rS2JJalZSUT",
          params
        }, {
          timeout: 30000,
          headers: {
            'Content-Type': 'application/json'
          }
        });

        console.log(`✅ API Response from ${endpoint}:`, response.data);
        return response.data;
      } catch (error) {
        console.error(`❌ API Error for ${endpoint} (Attempt ${attempt}/${retries}):`, error);
        
        if (attempt === retries) {
          if (error.response) {
            throw new Error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
          } else if (error.request) {
            throw new Error('Network error: No response from server');
          } else {
            throw new Error(`Request error: ${error.message}`);
          }
        }
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }, []);

  // Convert object to array
  const objectToArray = useCallback((obj) => {
    if (Array.isArray(obj)) return obj;
    if (obj && typeof obj === 'object') {
      return Object.keys(obj)
        .filter(key => !isNaN(key))
        .map(key => obj[key]);
    }
    return [];
  }, []);

  // Filter items by type
  const filterItemsByType = useCallback((itemsArray, type) => {
    console.log(`🔍 Filtering ${itemsArray.length} items for type: ${type}`);
    
    return itemsArray.filter(item => {
      if (!item || !item.slug || !item.title) {
        console.log('❌ Skipping invalid item:', item);
        return false;
      }

      const slug = item.slug.toLowerCase();
      const title = item.title.toLowerCase();

      switch (type) {
        case 'local':
          // Local: individual countries with country_code
          const isLocal = item.country_code && 
            item.country_code.length === 2 && // Country codes are 2 letters
            !title.includes(' and ') &&
            !title.includes('+') &&
            !title.includes('europe') &&
            !title.includes('asia') &&
            !title.includes('africa') &&
            !title.includes('global');
          
          return isLocal;

        case 'regional':
          // Regional: multi-country regions
          const isRegional = (
            slug.includes('eu') ||
            slug.includes('europe') ||
            slug.includes('asia') ||
            slug.includes('africa') ||
            slug.includes('caribbean') ||
            slug.includes('latin') ||
            title.includes('europe') ||
            title.includes('asia') ||
            title.includes('africa') ||
            title.includes('caribbean') ||
            title.includes('latin america') ||
            title.includes('middle east') ||
            title.includes(' and ') ||
            title.includes('+') ||
            !item.country_code || // Regional items often have empty country_code
            item.country_code.includes('+') ||
            item.country_code.includes(',')
          ) && !['discover', 'global'].includes(slug);
          
          return isRegional;

        case 'global':
          // Global: worldwide coverage
          const isGlobal = [
            'discover', 'global'
          ].includes(slug) ||
          title.includes('global') ||
          title.includes('discover') ||
          title.includes('worldwide');
          
          return isGlobal;

        default:
          return false;
      }
    });
  }, []);

  // Extract packages from item data
  const extractPackagesFromItem = useCallback((item) => {
    if (!item) return [];
    
    const packages = [];
    
    // Check if item has operators with packages (regional/global structure)
    if (item.operators) {
      const operatorsArray = objectToArray(item.operators);
      
      operatorsArray.forEach(operator => {
        if (operator?.packages) {
          const operatorPackages = objectToArray(operator.packages);
          
          operatorPackages.forEach(pkg => {
            if (pkg && pkg.type === 'sim') { // Only show main SIM packages, not topups
              packages.push({
                ...pkg,
                operator_title: operator.title,
                operator_style: operator.style,
                gradient_start: operator.gradient_start,
                gradient_end: operator.gradient_end,
                operator_image: operator.image
              });
            }
          });
        }
      });
    }
    
    // If no operators, try direct packages (local structure)
    if (packages.length === 0 && item.packages) {
      const directPackages = objectToArray(item.packages);
      packages.push(...directPackages.filter(pkg => pkg && pkg.type === 'sim'));
    }
    
    console.log(`📦 Extracted ${packages.length} packages from item`);
    return packages;
  }, [objectToArray]);

  // Load more countries for local tab
  const loadMoreCountries = useCallback(async () => {
    if (currentType !== 'local' || loadingMore || !hasMorePages) return;
    
    setLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const params = { 
        page: nextPage,
        type: 'local'
      };

      console.log(`📡 Loading more local countries - Page ${nextPage}`);
      const data = await makeApiRequest(params);

      if (data?.status && data.data) {
        const itemsArray = objectToArray(data.data);
        console.log(`📦 Received ${itemsArray.length} additional items`);

        const filteredItems = filterItemsByType(itemsArray, 'local');
        console.log(`🎯 Filtered to ${filteredItems.length} additional local items`);

        if (filteredItems.length > 0) {
          setItems(prevItems => [...prevItems, ...filteredItems]);
          setCurrentPage(nextPage);
          
          // Check if we should show more pages (assuming empty response means no more pages)
          if (filteredItems.length === 0) {
            setHasMorePages(false);
          }
        } else {
          setHasMorePages(false);
        }
      } else {
        setHasMorePages(false);
      }
    } catch (err) {
      console.error(`💥 Error loading more countries:`, err);
      setError(`Failed to load more countries: ${err.message}`);
    } finally {
      setLoadingMore(false);
    }
  }, [currentType, currentPage, loadingMore, hasMorePages, makeApiRequest, objectToArray, filterItemsByType]);

  // Main tab change handler
  const handleTabChange = useCallback(async (type) => {
    console.log(`🚀 Changing to ${type} tab`);
    
    setCurrentType(type);
    setCurrentSelection(null);
    setItems([]);
    setPackages([]);
    setError('');
    setCurrentPage(1);
    setHasMorePages(true);
    setShowPackagesOverlay(false);
    setLoading(true);

    try {
      const params = { 
        page: 1,
        type: type === 'local' ? 'local' : 'global'
      };

      console.log(`📡 Fetching ${type} data with params:`, params);
      const data = await makeApiRequest(params);

      if (data?.status && data.data) {
        const itemsArray = objectToArray(data.data);
        console.log(`📦 Received ${itemsArray.length} raw items`);

        const filteredItems = filterItemsByType(itemsArray, type);
        console.log(`🎯 Filtered to ${filteredItems.length} ${type} items`);

        if (filteredItems.length > 0) {
          setItems(filteredItems);
        } else {
          setError(`No ${type === 'local' ? 'countries' : 'regions'} found for this tab`);
        }
      } else {
        throw new Error('Invalid API response structure');
      }
    } catch (err) {
      console.error(`💥 Error in handleTabChange for ${type}:`, err);
      setError(`Failed to load ${type} data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [makeApiRequest, objectToArray, filterItemsByType]);

  // Handle item selection - Now opens packages overlay
  const handleItemSelect = useCallback(async (item) => {
    console.log(`🎯 Selecting item: ${item.title}`);
    setCurrentSelection(item);
    setLoadingPackages(true);
    setShowPackagesOverlay(true);

    try {
      // Extract packages from the item data directly
      const itemPackages = extractPackagesFromItem(item);
      setPackages(itemPackages);
      
      if (itemPackages.length === 0) {
        setError('No packages found for this selection');
      }
    } catch (err) {
      console.error(`💥 Error extracting packages:`, err);
      setError('Failed to load packages');
      setPackages([]);
    } finally {
      setLoadingPackages(false);
    }
  }, [extractPackagesFromItem]);

  // Close packages overlay
  const closePackagesOverlay = useCallback(() => {
    setShowPackagesOverlay(false);
    setPackages([]);
    setError('');
  }, []);

  // Image URL helper
  const getImageUrl = useCallback((imageData, fallbackText = '') => {
    if (imageData?.url) {
      return imageData.url;
    }
    const text = fallbackText.substring(0, 2).toUpperCase();
    return `https://via.placeholder.com/100x75/4A90E2/FFFFFF?text=${text}`;
  }, []);

  // Responsive grid columns for countries
  const getGridColumns = useCallback(() => {
    const width = window.innerWidth;
    if (width < 480) return 2;
    if (width < 768) return 3;
    if (width < 1024) return 4;
    if (width < 1280) return 5;
    if (width < 1600) return 6;
    return 7;
  }, []);

  // Format package data for display
  const formatPackageData = useCallback((pkg) => {
    return {
      id: pkg.id || 'unknown',
      title: pkg.title || 'Unnamed Package',
      data: pkg.data || `${Math.round(pkg.amount / 1024) || 0} GB`,
      days: pkg.day || 'N/A',
      price: pkg.price ? `$${pkg.price}` : 'N/A',
      operator: pkg.operator_title || 'Unknown Operator',
      gradient_start: pkg.gradient_start || '#0f1b3f',
      gradient_end: pkg.gradient_end || '#194281',
      image: pkg.operator_image || pkg.image
    };
  }, []);

  // Handle package click
  const handlePackageClick = useCallback((pkg) => {
    const formattedPackage = formatPackageData(pkg);
    setModalPackages([formattedPackage]);
    setShowModal(true);
  }, [formatPackageData]);

  // Initial load
  useEffect(() => {
    handleTabChange('local');
  }, [handleTabChange]);

  // Render loading spinner
  const renderLoadingSpinner = (message) => (
    <div className="loading-spinner-container">
      <div className="loading-spinner"></div>
      <p>{message}</p>
    </div>
  );

  // Render error message
  const renderErrorMessage = (message) => (
    <div className="error-message">
      <span>⚠️ {message}</span>
      <button 
        className="retry-btn"
        onClick={() => handleTabChange(currentType)}
      >
        Retry
      </button>
    </div>
  );

  return (
    <div className="esim-container">

      {/* Main Tabs */}
      <div className="esim-main-tabs">
        <button
          className={`main-tab ${currentType === 'local' ? 'active' : ''}`}
          onClick={() => handleTabChange('local')}
          disabled={loading}
        >
          Local eSIMs
        </button>
        <button
          className={`main-tab ${currentType === 'regional' ? 'active' : ''}`}
          onClick={() => handleTabChange('regional')}
          disabled={loading}
        >
          Regional eSIMs
        </button>
        <button
          className={`main-tab ${currentType === 'global' ? 'active' : ''}`}
          onClick={() => handleTabChange('global')}
          disabled={loading}
        >
          Global eSIMs
        </button>
      </div>

      {/* Items Grid (Countries/Regions) */}
      <div className="esim-country-tabs">
        {loading && renderLoadingSpinner(`Loading ${currentType === 'local' ? 'countries' : 'regions'}...`)}

        {error && renderErrorMessage(error)}

        {!loading && !error && items.length > 0 && (
          <>
            <div
              className="items-grid"
              style={{ gridTemplateColumns: `repeat(${getGridColumns()}, 1fr)` }}
            >
              {items.map((item, index) => (
                <div
                  key={`${item.slug}-${index}`}
                  className={`item-tab ${currentSelection?.slug === item.slug ? 'active' : ''}`}
                  onClick={() => handleItemSelect(item)}
                >
                  <img
                    src={getImageUrl(item.image, item.title)}
                    alt={item.title}
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/24x18/4A90E2/FFFFFF?text=${item.title.substring(0, 2)}`;
                    }}
                  />
                  <span>{item.title}</span>
                </div>
              ))}
            </div>

            {/* Load More Button - Only for Local Tab */}
            {currentType === 'local' && hasMorePages && (
              <div className="load-more-container">
                <button
                  className="load-more-btn"
                  onClick={loadMoreCountries}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <div className="small-spinner"></div>
                      Loading...
                    </>
                  ) : (
                    'Load More Countries'
                  )}
                </button>
              </div>
            )}

            {/* Loading More Spinner */}
            {loadingMore && (
              <div className="loading-more-container">
                <div className="small-spinner"></div>
                <span>Loading more countries...</span>
              </div>
            )}

            {/* No More Pages Message */}
            {currentType === 'local' && !hasMorePages && items.length > 0 && (
              <div className="no-more-countries">
                <span>All countries loaded</span>
              </div>
            )}
          </>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="no-data-message">
            No {currentType === 'local' ? 'countries' : 'regions'} available for this tab.
          </div>
        )}
      </div>

      {/* Packages Overlay - FIXED: Removed inline grid styles */}
      {showPackagesOverlay && (
        <div className="packages-overlay" onClick={closePackagesOverlay}>
          <div className="packages-overlay-content" onClick={(e) => e.stopPropagation()}>
            <div className="packages-overlay-header">
              <h3>{currentSelection?.title} - Available Packages</h3>
              <button className="close-overlay" onClick={closePackagesOverlay}>
                &times;
              </button>
            </div>

            <div className="packages-overlay-body">
              {loadingPackages && renderLoadingSpinner('Loading packages...')}

              {!loadingPackages && error && (
                <div className="error-message">
                  <span>⚠️ {error}</span>
                </div>
              )}

              {!loadingPackages && packages.length > 0 && (
                <div className="packages-grid-overlay">
                  {packages.map((pkg, index) => {
                    const formattedPkg = formatPackageData(pkg);
                    return (
                      <div
                        key={`${formattedPkg.id}-${index}`}
                        className="package-card-overlay"
                        onClick={() => handlePackageClick(pkg)}
                      >
                        <div 
                          className="package-header"
                          style={{
                            background: `linear-gradient(135deg, ${formattedPkg.gradient_start} 0%, ${formattedPkg.gradient_end} 100%)`
                          }}
                        >
                          <img
                            src={getImageUrl(formattedPkg.image, formattedPkg.operator)}
                            alt={formattedPkg.operator}
                            className="operator-logo"
                            onError={(e) => {
                              e.target.src = `https://via.placeholder.com/40x40/FFFFFF/000000?text=${formattedPkg.operator.substring(0, 2)}`;
                            }}
                          />
                          <h4>{formattedPkg.operator}</h4>
                        </div>
                        
                        <div className="package-body">
                          <h5>{formattedPkg.title}</h5>
                          <div className="package-details">
                            <div className="detail-item">
                              <span className="detail-icon">📊</span>
                              <span>{formattedPkg.data}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-icon">📅</span>
                              <span>{formattedPkg.days} days</span>
                            </div>
                            <div className="detail-item price">
                              <span className="detail-icon">💰</span>
                              <span>{formattedPkg.price}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!loadingPackages && packages.length === 0 && !error && (
                <div className="no-packages">No packages found for this selection.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Package Details Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setShowModal(false)}>
              &times;
            </button>

            <div className="modal-body">
              {modalPackages.length > 0 ? (
                <div className="modal-packages-grid">
                  {modalPackages.map((pkg, index) => (
                    <div key={index} className="modal-package-card">
                      <div
                        className="package-header"
                        style={{
                          background: `linear-gradient(135deg, ${pkg.gradient_start} 0%, ${pkg.gradient_end} 100%)`
                        }}
                      >
                        <img
                          src={getImageUrl(pkg.image, pkg.operator)}
                          alt={pkg.operator}
                          className="operator-logo"
                          onError={(e) => {
                            e.target.src = `https://via.placeholder.com/50x50/FFFFFF/000000?text=${pkg.operator.substring(0, 2)}`;
                          }}
                        />
                        <h3>{pkg.operator}</h3>
                      </div>

                      <div className="package-details">
                        <div className="detail-row">
                          <span className="detail-label">Package</span>
                          <span className="detail-value">{pkg.title}</span>
                        </div>

                        <div className="detail-row">
                          <span className="detail-label">Data</span>
                          <span className="detail-value">{pkg.data}</span>
                        </div>

                        <div className="detail-row">
                          <span className="detail-label">Validity</span>
                          <span className="detail-value">{pkg.days} Days</span>
                        </div>

                        <div className="detail-row price-row">
                          <span className="detail-label">Price</span>
                          <span className="detail-value price">{pkg.price}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-packages">No package details available.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;